import {
  DtoV1BoltResponseModelsAssetsAssetAccountScreenDataModelsAssetsAssetAccountInitialFormValues,
  ModelsAssetsAssetAccountInitialFormValues,
  ModelsAssetsAssetAccountScreenData,
  ModelsAssetsAssetStatement,
  ModelsAssetsDropdownFormValues,
  ModelsAssetsSourceDropdownOptions,
} from '~/__generated__/api';
import {
  GIFT_OF_EQUITY_OPTIONS,
  LOAN_PRODUCT_TYPE_BY_NAME,
  OCCUPANCY_TYPE_BY_NAME,
  RADIO_BUTTON_OPTIONS,
} from '~/common/constants';
import { convertToCustomFormat, formatDate } from '~/common/util/format';

import { assetDataType, formValuesType } from '../assetTypes';
import CashDeposit from './AssetYamlLayout/CashDeposit.yaml?raw';
import FHAGift from './AssetYamlLayout/FHAGift.yaml?raw';
import FHAGiftOfEquity from './AssetYamlLayout/FHAGiftOfEquity.yaml?raw';
import Gift from './AssetYamlLayout/Gift.yaml?raw';
import GiftOfEquity from './AssetYamlLayout/GiftOfEquity.yaml?raw';
import {
  BusinessAssetTypeArray,
  DefaultDate,
  DepositoryLiquidAssetTypeArray,
  DepositoryNonLiquidAssetTypeArray,
  EMDAssetTypeArray,
  ExpectedAssetTypeArray,
  GiftAssetType,
  GiftAssetTypeArray,
  GrantAssetTypeArray,
  NetProceeds,
  clearToCloseStatusId,
} from './enum';

/**
 * Compares the latest statementEndDate between old and new statement details data
 * @param {object} oldStatementDetails - Previous statement details data
 * @param {object} newStatementDetails - Current statement details data
 * @returns {object} Object containing hasChanged boolean and the latest dates from both datasets
 */
const hasLatestStatementEndDateChanged = (
  oldStatementDetails: formValuesType['statementDetails'],
  newStatementDetails: formValuesType['statementDetails'],
): {
  hasLatestStatementDateChanged: boolean;
} => {
  const getLatestStatementEndDate = (
    data: formValuesType['statementDetails'],
  ) => {
    if (!data || Object.keys(data).length === 0) return null;

    const dates = Object.values(data)
      .map((statement) => statement?.statementEndDate)
      .filter(Boolean);

    if (dates.length === 0) return null;

    const dateObjects = dates.map((dateStr) => new Date(dateStr));
    const latestDate = new Date(
      Math.max(...dateObjects.map((d) => d.getTime())),
    );

    return dates[
      dateObjects.findIndex((d) => d.getTime() === latestDate.getTime())
    ];
  };

  const oldLatestDate = formatDate(
    getLatestStatementEndDate(oldStatementDetails),
  );
  const newLatestDate = formatDate(
    getLatestStatementEndDate(newStatementDetails),
  );

  return { hasLatestStatementDateChanged: oldLatestDate !== newLatestDate };
};

/**
 *
 * @param {object} formValues
 * @param {object} data
 * @param {func} formatDate
 * @returns {object} userResponseSaveData with formatted date
 */
const processStatementDetails = (
  formValues: formValuesType,
  data: assetDataType,
  formatDate: (dateString: string) => string,
  loanStatus: string,
) => {
  const { statementDetails } = formValues;
  const userResponseSaveData = {};

  const result = hasLatestStatementEndDateChanged(
    data?.initialFormValues?.statementDetails,
    statementDetails,
  );

  for (const statementDocument in statementDetails) {
    const userResponseDataPerStatement = statementDetails[statementDocument];

    if (loanStatus === 'File Imported') {
      delete userResponseDataPerStatement?.largeDeposit;
      delete userResponseDataPerStatement?.debtsAndDeduction;
    }

    const defaultValues = {
      donorNameInput: '',
      giftAmountInput: '0',
      donorRelationshipInput: '',
      donorAddressInput: '',
    };
    Object.keys(defaultValues).forEach((key) => {
      if (userResponseDataPerStatement[key] === null) {
        userResponseDataPerStatement[key] = defaultValues[key];
      }
    });

    let userResponseAfterDateFormat = { ...userResponseDataPerStatement };
    if (userResponseDataPerStatement.statementStartDate) {
      userResponseAfterDateFormat = {
        ...userResponseDataPerStatement,
        statementStartDate: formatDate(
          userResponseDataPerStatement.statementStartDate,
        ),
        statementEndDate: formatDate(
          userResponseDataPerStatement.statementEndDate,
        ),
      };
    }
    const isBalanceComparisonSelectionMissing =
      !data?.initialFormValues?.statementDetails[statementDocument]
        ?.balanceComparisonSelection;
    const isBalanceComparisonSelectionUpdate =
      userResponseDataPerStatement.balanceComparisonSelection === 'Update';
    const isBalanceUpdateValueChanged =
      userResponseDataPerStatement.balanceUpdateValue !==
      data?.initialFormValues?.statementDetails[statementDocument]
        ?.balanceUpdateValue;

    const shouldMarkBalanceUpdateDirty =
      (isBalanceComparisonSelectionMissing &&
        isBalanceComparisonSelectionUpdate) ||
      (isBalanceComparisonSelectionUpdate && isBalanceUpdateValueChanged) ||
      result.hasLatestStatementDateChanged;

    userResponseAfterDateFormat = {
      ...userResponseAfterDateFormat,
      isBalanceUpdateDirty: shouldMarkBalanceUpdateDirty,
    };

    userResponseSaveData[statementDocument] = userResponseAfterDateFormat;
  }

  return userResponseSaveData;
};

/**
 * @param {string} sourceString - Format: "AssetLdId:1/**AssetId:8476160/**AssetType:Checking/**DisplayName:Old Kentucky Home Bank - 6622"
 * @returns {object} parsed object with assetLdId, assetId, assetType, and displayName
 */
const parseAssetSourceString = (sourceString: string) => {
  if (!sourceString) return null;

  const parts = sourceString.split('/**');
  const result: {
    assetLdId?: string;
    assetId?: string;
    assetType?: string;
    displayName?: string;
  } = {};

  parts.forEach((part) => {
    const [key, value] = part.split(':');
    if (key && value) {
      // Converting key to camelCase (AssetLdId -> assetLdId)
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      result[camelKey] = value;
    }
  });

  return result;
};

/**
 *
 * @param assetDetails
 * @param sourceDropdownOptions
 * @param sourceOption
 * @returns a concatenated string or empty string depending on the dropdown options and assetId from the sourceOption
 */
const populateSourceDropdownOptions = (
  assetDetails: {
    [key: string]: string;
  },
  sourceDropdownOptions: ModelsAssetsSourceDropdownOptions[],
  sourceOption: string,
) => {
  const asset = parseAssetSourceString(assetDetails?.[sourceOption]);
  const associatedSourceOption = sourceDropdownOptions?.find(
    (sourceDropdownOption) => sourceDropdownOption?.assetId === +asset?.assetId,
  );
  if (asset?.displayName === 'Not Provided' || asset?.displayName === 'Gift') {
    return assetDetails?.[sourceOption];
  } else if (asset?.assetId) {
    return associatedSourceOption
      ? `AssetLdId:${associatedSourceOption?.assetLdId}/**AssetId:${associatedSourceOption?.assetId}/**AssetType:${associatedSourceOption?.assetType}/**DisplayName:${associatedSourceOption?.displayName}`
      : '';
  }
};

/**
 *
 * @param {object} assets
 * @param {object} assetDetails
 * @returns {object} modified assetDetails with emd amount
 */
export const addDefaultEmdAmount = (
  screenData: ModelsAssetsAssetAccountScreenData,
  initialFormValues: ModelsAssetsAssetAccountInitialFormValues,
  grantDefaultData: {
    giftSourceDropdownSelection?: string;
    howGiftIsBeingTransferred?: string;
  },
  statements: ModelsAssetsAssetStatement,
) => {
  const assets = screenData?.assets;
  const assetDetails = initialFormValues?.assetDetails;
  const isEMDAsset = EMDAssetTypeArray.includes(assets?.assetType);
  const isCashDepositOrExpectedAsset =
    assets?.assetType === GiftAssetType.CASH_DEPOSIT ||
    ExpectedAssetTypeArray.includes(assets?.assetType);
  const isGrantType = GrantAssetTypeArray.includes(assets?.assetType);
  const giftAmount = assetDetails?.giftAmountInput
    ? assetDetails?.giftAmountInput
    : statements?.extractedData?.giftAmount?.value || '';
  const emdSourceDropdownOptions = screenData?.emdSourceDropdownOptions;

  if (isEMDAsset) {
    const donorName =
      assetDetails?.donorNameInput ||
      [
        statements?.extractedData?.donorFirstName?.value || '',
        statements?.extractedData?.donorMiddleInitial?.value || '',
        statements?.extractedData?.donorLastName?.value || '',
      ]
        .filter(Boolean)
        .join(' ');

    const donorAddress =
      assetDetails?.donorAddressInput ||
      [
        statements?.extractedData?.donorAddress?.value || '',
        statements?.extractedData?.donorCity?.value || '',
        statements?.extractedData?.donorState?.value || '',
        statements?.extractedData?.donorZipCode?.value || '',
      ]
        .filter(Boolean)
        .join(', ');

    const emdSourceOption = populateSourceDropdownOptions(
      assetDetails,
      emdSourceDropdownOptions,
      'emdSourceOption',
    );

    return {
      ...assetDetails,
      balanceUpdateValue: assets?.balance,
      donorNameInput: donorName,
      donorAddressInput: donorAddress,
      giftAmountInput: giftAmount,
      emdSourceOption: emdSourceOption,
    };
  }

  if (isCashDepositOrExpectedAsset) {
    const cashDepositSourceDropdownOptions =
      screenData?.cashDepositSourceDropdownOptions;
    const cashDepositSource = populateSourceDropdownOptions(
      assetDetails,
      cashDepositSourceDropdownOptions,
      'cashDepositSource',
    );
    return {
      ...assetDetails,
      balanceUpdateValue: assets?.balance,
      cashDepositSource: cashDepositSource,
    };
  }

  if (isGrantType)
    return {
      ...assetDetails,
      balanceUpdateValue: assets?.balance,
      entityNameUpdate: assets?.bankName,
      giftSourceDropdownOption: grantDefaultData?.giftSourceDropdownSelection,
      howGiftIsBeingTransferred: grantDefaultData?.howGiftIsBeingTransferred,
      is1031Account: false,
    };

  return {
    ...assetDetails,
  };
};

/**
 *
 * @param {object} data
 * @param {string} valueName
 * @param {number} documentId
 * @returns {boolean }  flag to show/hide the field array based on the if transaction exists in the statement
 */
export const flagForAssetFieldArray = (
  data: DtoV1BoltResponseModelsAssetsAssetAccountScreenDataModelsAssetsAssetAccountInitialFormValues,
  valueName: string,
  documentId: number,
) => {
  const statementValues =
    data?.initialFormValues?.statementDetails?.[`1-${documentId}`]?.[valueName];

  return statementValues?.length > 0;
};

/**
 *
 * @param {object} data
 * @returns {Array} initialForm with list of documentIds with next large deposit amount greater than threshold
 */
export const dirtyForms = (
  data: DtoV1BoltResponseModelsAssetsAssetAccountScreenDataModelsAssetsAssetAccountInitialFormValues,
) => {
  const statementDetails = data?.initialFormValues?.statementDetails;
  const initialForm = [];

  for (const statementDocumentId in statementDetails) {
    const statementInitialValue = statementDetails[statementDocumentId];
    const nextLargeDepositAmount = +statementInitialValue?.nextLargestDeposit;
    if (
      nextLargeDepositAmount > data?.screenData?.assets?.largeDepositThreshold
    ) {
      const documentId = statementDocumentId.split('-');
      initialForm.push(+documentId[1]);
    }
  }
  return initialForm;
};

/**
 *
 * @param {string} productType
 * @returns {object} yaml layout based on productType for GiftOfEquity
 */
export const buildGiftOfEquityYamlLayoutByProductType = (
  productType: string,
) =>
  productType === LOAN_PRODUCT_TYPE_BY_NAME.FHA
    ? FHAGiftOfEquity
    : GiftOfEquity;

/**
 *
 * @param {string} productType
 * @returns {object} yaml layout based on productType for Gift
 */
export const buildGiftYamlLayoutByProductType = (productType: string) =>
  productType === LOAN_PRODUCT_TYPE_BY_NAME.FHA ? FHAGift : Gift;

/**
 *
 * @param {boolean} isEarnestMoneyDepositAGift
 * @param {string} productType
 * @returns {object} yaml layout based on isEarnestMoneyDepositAGift for CashDeposit
 */
export const buildCashDepositYamlLayout = (
  isEarnestMoneyDepositAGift: boolean,
  productType: string,
) =>
  isEarnestMoneyDepositAGift
    ? buildGiftYamlLayoutByProductType(productType)
    : CashDeposit;

/**
 *
 * @param {object} statementDetails
 * @param {object} statements
 * @param {object} assetType
 * @returns {object} initialFormValues with default date if extraction is successful and statement date in userResponseData is not available
 */

/**
 * Helper to process a single statement detail entry for addDefaultDateIfExtractionSuccess.
 */
function processStatementDetailEntry(
  key: string,
  statementDetails: formValuesType['statementDetails'],
  statements: ModelsAssetsAssetStatement[],
) {
  const documentId = key.split('-');
  let userResponseDataPerStatement = statementDetails[key];
  const statementData = statements.find(
    (statement) => statement.documentId === +documentId[1],
  );

  if (
    statementData?.extractedData?.startDate?.value !== 'Not Available' &&
    !userResponseDataPerStatement?.statementStartDate
  ) {
    userResponseDataPerStatement = {
      ...userResponseDataPerStatement,
      statementStartDate: statementData?.extractedData?.startDate?.value,
    };
  }

  if (
    statementData?.extractedData?.endDate?.value !== 'Not Available' &&
    !userResponseDataPerStatement?.statementEndDate
  ) {
    userResponseDataPerStatement = {
      ...userResponseDataPerStatement,
      statementEndDate: statementData?.extractedData?.endDate?.value,
    };
  }

  return userResponseDataPerStatement;
}

function shouldResetDonorRelationship(assetType: string) {
  return (
    !BusinessAssetTypeArray.includes(assetType) &&
    !DepositoryNonLiquidAssetTypeArray.includes(assetType) &&
    !DepositoryLiquidAssetTypeArray.includes(assetType) &&
    assetType !== GiftAssetType.GIFT
  );
}

function handleGiftAssetType(
  statementDetails: formValuesType['statementDetails'],
  statements: ModelsAssetsAssetStatement[],
  giftRelationshipFormValues: ModelsAssetsDropdownFormValues[],
  productType: string,
  howGiftIsBeingTransferred: string,
  loanStatusId: number,
) {
  let modifiedStatementDetails = { ...statementDetails };
  const shouldSetEvidenceOfReceiptAtClosingQuestion =
    howGiftIsBeingTransferred === 'ToTitleAtClosing' &&
    loanStatusId >= clearToCloseStatusId;

  if (shouldSetEvidenceOfReceiptAtClosingQuestion) {
    modifiedStatementDetails = {
      [`1-${statements?.[0]?.documentId}`]: {
        ...statementDetails?.[`1-${statements?.[0]?.documentId}`],
        hasEvidenceOfReceiptFromTitleBeenProvidedAtClosing:
          statementDetails?.[`1-${statements?.[0]?.documentId}`]
            ?.hasEvidenceOfReceiptFromTitleBeenProvidedAtClosing || 'No',
      },
    };
  }

  modifiedStatementDetails = resetDonorRelationshipIfNotInDropdown(
    modifiedStatementDetails,
    statements,
    giftRelationshipFormValues,
    productType,
  );

  return modifiedStatementDetails;
}

export const addDefaultDateIfExtractionSuccess = (
  statementDetails: formValuesType['statementDetails'],
  screenData: ModelsAssetsAssetAccountScreenData,
  loanStatusId: number,
) => {
  const { statements, assets, giftRelationshipDropdown, loanAssetInformation } =
    screenData || {};
  const assetType = assets?.assetType;
  const productType = loanAssetInformation?.productType;
  const giftRelationshipFormValues = giftRelationshipDropdown;
  const firstStatementId = statements?.[0]?.documentId;
  const howGiftIsBeingTransferred =
    statementDetails?.[`1-${firstStatementId}`]?.howGiftIsBeingTransferred;

  if (shouldResetDonorRelationship(assetType)) {
    return resetDonorRelationshipIfNotInDropdown(
      statementDetails,
      statements,
      giftRelationshipFormValues,
      productType,
    );
  }

  if (assetType === GiftAssetType.GIFT) {
    return handleGiftAssetType(
      statementDetails,
      statements,
      giftRelationshipFormValues,
      productType,
      howGiftIsBeingTransferred,
      loanStatusId,
    );
  }

  return Object.fromEntries(
    Object.keys(statementDetails).map((key) => [
      key,
      processStatementDetailEntry(key, statementDetails, statements),
    ]),
  );
};

/**
 * @param {number} value
 * @returns {string} occupancy type based on value
 */
export const occupancyType = (value: number) => {
  if (value === 1) {
    return 'primaryResidence';
  } else if (value === 2) {
    return 'investmentProperty';
  } else if (value === 3) {
    return 'secondaryResidence';
  }
};

/**
 * @param {array} invalidValues
 * @param {array} valuesToCheck
 * @returns {boolean} true if value is valid
 */
export const checkIsValidValue = (invalidValues = [], valuesToCheck = []) => {
  if (!invalidValues.length || !valuesToCheck.length) return true;

  for (const value of valuesToCheck) {
    if (invalidValues.includes(value)) {
      return false;
    }
  }

  return true;
};

/**
 *
 * @param {object} allValues
 * @param {string} source
 * @param {string} propertyToCheck
 * @returns boolean value from source
 */
export const getValueFromSource = (
  allValues: {
    documentId?: string;
    statementDetails?: formValuesType['statementDetails'];
    assetDetails?: formValuesType['assetDetails'];
  },
  source = '',
  propertyToCheck = '',
) => {
  if (source === 'gift') {
    const docId = allValues?.documentId;
    const newDocId = `1-${docId}`;
    const isMissing =
      allValues?.statementDetails?.[newDocId]?.[propertyToCheck];
    return isMissing;
  }
  return allValues?.assetDetails?.[propertyToCheck];
};

/**
 *
 * @param {object} assetDetails
 * @param {string} assetType
 * @returns assetDetails with formatted values
 */
const processAssetDetails = (
  assetDetails: {
    disbursementDate?: string;
    is1031Account?: boolean;
  },
  assetType: string,
) => {
  if (assetType === NetProceeds) {
    return {
      ...assetDetails,
      disbursementDate:
        formatDate(assetDetails?.disbursementDate) === DefaultDate
          ? null
          : convertToCustomFormat(assetDetails?.disbursementDate),
      is1031Account: assetDetails?.is1031Account
        ? String(assetDetails?.is1031Account)
        : 'false',
    };
  } else {
    return {
      ...assetDetails,
    };
  }
};

/**
 *
 * @param {number} nextLargestDeposit
 * @param {number} largeDepositThreshold
 * @returns boolean value
 */
export const checkIfNextLargestDepositFirstEntry = (
  nextLargestDeposit: number,
  largeDepositThreshold: number,
) => {
  if (!nextLargestDeposit) return false;
  const nextlargestDepositAnswer = +nextLargestDeposit;

  return nextlargestDepositAnswer > largeDepositThreshold;
};

/**
 * @param {array} snippets
 * @returns post call to display bounding boxes
 */
export const handlePDFOnFocus = (snippets) => {
  window.postMessage(
    {
      type: 'pdfCitation',
      payload: [snippets],
    },
    globalThis.location.origin,
  );
};

/**
 *
 * @param formValues
 * @param data
 * @param isDocumentProvidedCheck
 * @param hasAppraisalWaiver
 * @param loanInformation
 * @param assetType
 * @param loanOccupancyType
 * @param isRefinance
 * @returns bool value of if asset should be deleted
 */

type checkShouldDeleteAssetProps = {
  formValues: {
    assetDetails?: formValuesType['assetDetails'];
    statementDetails?: formValuesType['statementDetails'];
  };
  data: {
    screenData: {
      documentId: number;
      assetsFeatureFlags: {
        assetFeatureFlags: {
          AssetScreenGiftDonorDropdownEnhancementEffectiveDate: boolean;
        };
      };
      assets: {
        isDocumentProvided: boolean;
        assetType: string;
      };
      loanAssetInformation: {
        isRefinance?: boolean;
        hasAppraisalWaiver?: boolean;
        occupancyType?: number;
      };
    };
  };
  loanInformation: {
    isDU?: boolean;
    productType?: string;
  };
};

const checkEachStatementForDoesAtLeastOneBorrowerHaveOwnership = (
  statementDetails?: formValuesType['statementDetails'],
) => {
  for (const statementDetail of Object.values(statementDetails)) {
    if (statementDetail?.doesAtLeastOneBorrowerHaveOwnership === 'No') {
      return true;
    }
  }
  return false;
};

export const checkShouldDeleteAsset = (
  formValues: checkShouldDeleteAssetProps['formValues'],
  data: checkShouldDeleteAssetProps['data'],
  loanInformation: checkShouldDeleteAssetProps['loanInformation'],
) => {
  const documentIdParsed = `1-${data?.screenData?.documentId}`;
  const statementDetail = formValues.statementDetails[documentIdParsed];
  const isRefinance = data?.screenData?.loanAssetInformation?.isRefinance;
  const isDocumentProvidedCheck = data?.screenData?.assets?.isDocumentProvided;
  const hasAppraisalWaiver =
    data?.screenData?.loanAssetInformation?.hasAppraisalWaiver;
  const assetType = data?.screenData?.assets?.assetType;
  const loanOccupancyType =
    data?.screenData?.loanAssetInformation?.occupancyType;

  const shouldDeleteAssetNoDocumentsProvided =
    !isDocumentProvidedCheck &&
    formValues.assetDetails.willProvideDocument === RADIO_BUTTON_OPTIONS[1];

  const shouldDeleteAssetLoanHasAppraisalWaiver =
    hasAppraisalWaiver &&
    !!documentIdParsed &&
    (loanInformation?.isDU ||
      loanInformation?.productType === LOAN_PRODUCT_TYPE_BY_NAME.FHA) &&
    statementDetail?.appraisalWaiver ===
      GIFT_OF_EQUITY_OPTIONS.APPRAISAL_WAIVER;

  const shouldDeleteGiftAssetDonorRelationshipOther =
    GiftAssetTypeArray.includes(assetType) &&
    statementDetail?.donorRelationshipInput === 'Other' &&
    loanInformation?.productType === LOAN_PRODUCT_TYPE_BY_NAME.CONVENTIONAL &&
    data.screenData.assetsFeatureFlags.assetFeatureFlags
      .AssetScreenGiftDonorDropdownEnhancementEffectiveDate === true;

  const shouldDeleteAssetSellerAndDonorNamesMismatch =
    !!documentIdParsed &&
    statementDetail?.sellerNameMatch === RADIO_BUTTON_OPTIONS[1];

  const shouldDeleteAssetInvestmentGift =
    GiftAssetTypeArray.includes(assetType) &&
    loanOccupancyType === OCCUPANCY_TYPE_BY_NAME.INVESTMENT_PROPERTY;

  const shouldDeleteAssetGiftOfEquityRefinance =
    isRefinance && assetType === GiftAssetType.GIFT_OF_EQUITY;

  const shouldDeleteExpectedAssets =
    formValues?.assetDetails?.deleteExpectedAssets === true;

  const shouldDeleteIfhasAffiliationWithInterestedPartyTrue =
    (formValues?.assetDetails?.hasAffiliationWithInterestedParty ===
      RADIO_BUTTON_OPTIONS[0] ||
      statementDetail?.hasAffiliationWithInterestedParty ===
        RADIO_BUTTON_OPTIONS[0]) &&
    !EMDAssetTypeArray.includes(data?.screenData?.assets?.assetType);

  const shouldDeleteBusinessAsset = BusinessAssetTypeArray.includes(assetType)
    ? checkEachStatementForDoesAtLeastOneBorrowerHaveOwnership(
        formValues?.statementDetails,
      )
    : false;

  return (
    shouldDeleteAssetNoDocumentsProvided ||
    shouldDeleteAssetLoanHasAppraisalWaiver ||
    shouldDeleteGiftAssetDonorRelationshipOther ||
    shouldDeleteAssetSellerAndDonorNamesMismatch ||
    shouldDeleteAssetInvestmentGift ||
    shouldDeleteAssetGiftOfEquityRefinance ||
    shouldDeleteExpectedAssets ||
    shouldDeleteIfhasAffiliationWithInterestedPartyTrue ||
    shouldDeleteBusinessAsset
  );
};

export const checkShouldDeleteDisclosedExpectedAsset = (
  formValues: checkShouldDeleteAssetProps['formValues'],
  data: checkShouldDeleteAssetProps['data'],
) => {
  const isExpectedAsset = ExpectedAssetTypeArray.includes(
    data.screenData.assets.assetType,
  );

  const isDisclosedExpectedAsset =
    formValues?.assetDetails?.brokerDisclosedExpectedAssetsCheckbox === true;

  const shouldDeleteDisclosedExpectedAsset =
    formValues?.assetDetails?.deleteExpectedAssets === true;

  return (
    isExpectedAsset &&
    isDisclosedExpectedAsset &&
    shouldDeleteDisclosedExpectedAsset
  );
};

export const sanitizeformValues = (
  loanStatus: string,
  formValues?: {
    assetDetails: formValuesType['assetDetails'];
    statementDetails: formValuesType['statementDetails'];
  },
  isCashDepositNotGift?: boolean,
  data?: assetDataType,
) => {
  const assetType = data?.screenData?.assets?.assetType;
  const isDocumentProvidedCheck = data?.screenData?.assets?.isDocumentProvided;

  let assetDetails = null;
  let userResponseSaveData = {};

  if (
    BusinessAssetTypeArray.includes(assetType) ||
    DepositoryNonLiquidAssetTypeArray.includes(assetType)
  ) {
    assetDetails = { ...formValues.assetDetails };
  }

  if (
    isDocumentProvidedCheck &&
    !isCashDepositNotGift &&
    [
      DepositoryLiquidAssetTypeArray,
      GiftAssetTypeArray,
      BusinessAssetTypeArray,
      DepositoryNonLiquidAssetTypeArray,
    ].some((arr) => arr.includes(assetType))
  ) {
    userResponseSaveData = processStatementDetails(
      formValues,
      data,
      formatDate,
      loanStatus,
    );
  } else if (
    EMDAssetTypeArray.includes(assetType) ||
    GrantAssetTypeArray.includes(assetType) ||
    assetType === NetProceeds ||
    assetType === GiftAssetType.CASH_DEPOSIT ||
    ExpectedAssetTypeArray.includes(assetType)
  ) {
    const saveAssetDetails = processAssetDetails(
      formValues?.assetDetails,
      assetType,
    );
    assetDetails = { ...saveAssetDetails };
  } else {
    assetDetails = {
      willProvideDocument: formValues.assetDetails.willProvideDocument,
    };
  }

  return { assetDetails, userResponseSaveData };
};
function resetDonorRelationshipIfNotInDropdown(
  statementDetails: formValuesType['statementDetails'],
  statements: ModelsAssetsAssetStatement[],
  giftRelationshipFormValues: ModelsAssetsDropdownFormValues[],
  productType?: string,
) {
  const donorRelationship =
    statementDetails?.[`1-${statements?.[0]?.documentId}`]
      ?.donorRelationshipInput;

  if (
    donorRelationship &&
    !giftRelationshipFormValues.some(
      (option) => option.value === donorRelationship,
    ) &&
    productType === LOAN_PRODUCT_TYPE_BY_NAME.CONVENTIONAL
  ) {
    statementDetails[`1-${statements?.[0]?.documentId}`] = {
      ...statementDetails[`1-${statements?.[0]?.documentId}`],
      donorRelationshipInput: '',
    };
  }
  return statementDetails;
}
